import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Request a number" — the customer's working path to a phone number (customer-portal review #49,
 * fix round 2026-09-25).
 *
 * A customer cannot take a number from the console: issuing numbers waits on product question Q4,
 * connecting a trunk is not built, and there is no bring-your-own flow. What works is ASKING: the
 * dialog records the agent that should answer, the country and anything WeTalk should know
 * (`POST /v1/numbers/requests`); an operator reads it and assigns the number. A request still
 * waiting is a "Setting up" row of the number list (numbers-sip-sync); one WeTalk fulfilled is the
 * number itself — `connected` is derived by the API from the number actually bound to that agent,
 * so it cannot claim a number that is not there.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, Field, Select, Textarea } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiErrorText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, voiceAgentListQuery } from "../../api/query.js";
import type { ConsoleGateway } from "../../api/gateway.js";
import type { NumberRequestView } from "../../api/slice/pending-request.js";

/**
 * The countries the dialog offers, by name, with their ISO codes. Not a promise that WeTalk can
 * issue in each — the operator answers that — only the places a customer is likely to ask for.
 */
export const NUMBER_REQUEST_COUNTRY = () => ([
  ["GR", portalText("operations", "greece_4902a456")],
  ["CY", portalText("operations", "cyprus_852addab")],
  ["IT", portalText("operations", "italy_ad79ef0f")],
  ["ES", portalText("operations", "spain_20a8df9b")],
  ["PT", portalText("operations", "portugal_a495190b")],
  ["FR", portalText("operations", "france_e3772ac4")],
  ["DE", portalText("operations", "germany_17d53e0e")],
  ["NL", portalText("operations", "netherlands_fb61c8a8")],
  ["BE", portalText("operations", "belgium_5cb4c9d8")],
  ["IE", portalText("operations", "ireland_eb2131ec")],
  ["GB", portalText("operations", "united_kingdom_9769121f")],
  ["US", portalText("operations", "united_states_768685ca")],
  ["CA", portalText("operations", "canada_cd6a7b87")],
]);

export const numberRequestListQuery = (gateway: ConsoleGateway) => ({
  queryKey: QUERY_KEY.numberRequest,
  queryFn: () => gateway.listNumberRequests(),
});

export function countryName(code: string): string {
  return NUMBER_REQUEST_COUNTRY().find(([one]) => one === code)?.[1] ?? code;
}

export interface NumberRequestDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function NumberRequestDialog({ open, onClose }: NumberRequestDialogProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const voiceAgentList = useQuery(voiceAgentListQuery(gateway));
  const [voiceAgentId, setVoiceAgentId] = useState("");
  const [country, setCountry] = useState("");
  const [note, setNote] = useState("");
  const [problem, setProblem] = usePortalMessage();
  const [sent, setSent] = useState<NumberRequestView | null>(null);

  const voiceAgents = voiceAgentList.data ?? [];
  const chosenVoiceAgentId = voiceAgentId === "" ? (voiceAgents[0]?.voiceAgentId ?? "") : voiceAgentId;

  const send = useMutation({
    mutationFn: () => gateway.requestPhoneNumber({ voiceAgentId: chosenVoiceAgentId, country, note }),
    onSuccess: async (made) => {
      setSent(made);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.numberRequest });
    },
    onError: (error: unknown) => {
      setProblem(() => (apiErrorText(error, portalText("operations", "the_request_could_not_be_sent_nothing_has_changed_4512162e"))));
    },
  });

  const close = (): void => {
    setProblem(null);
    setSent(null);
    setNote("");
    onClose();
  };

  const submit = (): void => {
    if (chosenVoiceAgentId === "") {
      setProblem(() => (portalText("operations", "create_an_agent_first_a_number_is_set_up_for_the_agent_tha_19703a91")));
      return;
    }
    if (country === "") {
      setProblem(() => (portalText("operations", "choose_the_country_the_number_should_be_in_7762ea41")));
      return;
    }
    setProblem(null);
    send.mutate();
  };

  return (
    <Dialog
      open={open}
      title={portalText("operations", "request_a_number_705e332d")}
      onClose={close}
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            {sent === null ? portalText("operations", "cancel_77dfd213") : portalText("operations", "close_bbfa773e")}
          </Button>
          {sent === null ? (
            <Button variant="primary" disabled={send.isPending || voiceAgents.length === 0} onClick={submit}>
              {send.isPending ? portalText("operations", "sending_cf765512") : portalText("operations", "send_request_a89d6417")}
            </Button>
          ) : null}
        </>
      }
    >
      {sent !== null ? (
        <p className="c-note" role="status">
          {portalText("operations", "sent_the_wetalk_team_sets_up_a_148d248b")}{countryName(sent.country)} {portalText("operations", "number_for_4cdcf027")}{" "}
          {sent.voiceAgentName} {portalText("operations", "and_connects_it_it_appears_on_this_page_and_on_the_agent_s_97840a09")}</p>
      ) : voiceAgentList.data !== undefined && voiceAgents.length === 0 ? (
        <p className="c-note">
          {portalText("operations", "create_an_agent_first_a_number_is_set_up_for_the_agent_tha_19703a91")}</p>
      ) : (
        <>
          <p className="c-note">
            {portalText("operations", "numbers_are_set_up_by_wetalk_for_now_tell_us_which_agent_s_a18cca4b")}</p>
          <Field label={portalText("operations", "agent_that_answers_737d3bb5")} {...(problem === null ? {} : { error: problem })}>
            {(control) => (
              <Select
                {...control}
                value={chosenVoiceAgentId}
                onChange={(event) => {
                  const next = event.currentTarget.value;
                  setVoiceAgentId(next);
                }}
              >
                {voiceAgents.map((one) => (
                  <option key={one.voiceAgentId} value={one.voiceAgentId}>
                    {one.displayName}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={portalText("operations", "country_d523ebbd")}>
            {(control) => (
              <Select
                {...control}
                value={country}
                onChange={(event) => {
                  const next = event.currentTarget.value;
                  setCountry(next);
                }}
              >
                <option value="">{portalText("operations", "choose_a_country_7843c8c2")}</option>
                {NUMBER_REQUEST_COUNTRY().map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={portalText("operations", "anything_we_should_know_0bb02313")}
            description={portalText("operations", "a_number_you_already_have_and_want_to_keep_your_own_phone_45210859")}
          >
            {(control) => (
              <Textarea
                {...control}
                rows={3}
                maxLength={2000}
                value={note}
                onChange={(event) => {
                  const next = event.currentTarget.value;
                  setNote(next);
                }}
              />
            )}
          </Field>
        </>
      )}
    </Dialog>
  );
}
